import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import toast from "react-hot-toast";
import apiClient from "../../../services/apiClient";
import PageLoader from "../../Loading/PageLoader";

const TranscriberConfigsList = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  const [formData, setFormData] = useState({
    provider: "",
    language: "",
    model: "",
    active: true,
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  // Fetch all configs
  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/transcriber-configs");
      setConfigs(response.data || []);
    } catch (err) {
      toast.error("Failed to fetch transcriber configs");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form changes
  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]:
        e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));
  };

  // Create new config
  const handleCreate = async () => {
    if (!formData.provider) return toast.error("Provider is required");
    setLoading(true);
    try {
      await apiClient.post("/transcriber-configs", formData);
      toast.success("Transcriber created successfully");
      handleCloseModal();
      fetchConfigs();
    } catch (err) {
      toast.error("Failed to create transcriber config");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Edit config
  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      provider: config.provider,
      language: config.language,
      model: config.model,
      active: config.active,
    });
    setOpenModal(true);
  };

  // Update config
  const handleUpdate = async () => {
    if (!editingConfig) return;
    setLoading(true);
    try {
      await apiClient.put(`/transcriber-configs/${editingConfig._id}`, formData);
      toast.success("Transcriber updated successfully");
      handleCloseModal();
      fetchConfigs();
    } catch (err) {
      toast.error("Failed to update transcriber config");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Delete config
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this transcriber?"))
      return;
    setLoading(true);
    try {
      await apiClient.delete(`/transcriber-configs/${id}`);
      toast.success("Transcriber deleted successfully");
      fetchConfigs();
    } catch (err) {
      toast.error("Failed to delete transcriber config");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Toggle active status
  const handleToggleStatus = async (id) => {
    setLoading(true);
    try {
      await apiClient.patch(`/transcriber-configs-status/${id}`);
      toast.success("Status toggled successfully");
      fetchConfigs();
    } catch (err) {
      toast.error("Failed to toggle status");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingConfig(null);
    setFormData({
      provider: "",
      language: "",
      model: "",
      active: true,
    });
  };

  const columns = [
    { accessorKey: "provider", header: "Provider" },
    { accessorKey: "language", header: "Language" },
    { accessorKey: "model", header: "Model" },
    {
      accessorKey: "active",
      header: "Status",
      Cell: ({ row }) => (
        <Switch
          checked={row.original.active}
          onChange={() => handleToggleStatus(row.original._id)}
          size="small"
        />
      ),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      size: 150,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" onClick={() => handleEdit(row.original)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(row.original._id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading && configs.length === 0) return <PageLoader />;

  return (

    <>
    
    {/* Header */}
    <div className="pageTitle1 d-flex justify-content-between align-items-center px-5 py-4">
      <h3>Transcriber Configs</h3>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setOpenModal(true)}
      >
        New Config
      </Button>
    </div>

    <Box sx={{ p: 3 }}>
      
      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search transcribers..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
        />
      </Box>

      {/* Table */}
      <MaterialReactTable
        columns={columns}
        data={configs.filter((c) =>
          c.provider?.toLowerCase().includes(searchText.toLowerCase())
        )}
        enableRowActions={false}
        enableColumnActions={false}
        enableSorting
        enablePagination
        initialState={{
          pagination: { pageSize: 10, pageIndex: 0 },
        }}
      />

      {/* Popup Form */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingConfig
            ? "Edit Transcriber Config"
            : "Create New Transcriber Config"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 1, display: "grid", gap: 2 }}>
            {["provider", "language", "model"].map((field) => (
              <TextField
                key={field}
                label={field.toUpperCase()}
                name={field}
                value={formData[field]}
                onChange={handleChange}
                fullWidth
              />
            ))}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Typography>Active</Typography>
              <Switch
                checked={formData.active}
                onChange={(e) =>
                  setFormData((p) => ({ ...p, active: e.target.checked }))
                }
              />
              <Chip
                label={formData.active ? "Active" : "Inactive"}
                color={formData.active ? "success" : "default"}
                size="small"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={editingConfig ? handleUpdate : handleCreate}
          >
            {editingConfig ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>


    </>

  );
};

export default TranscriberConfigsList;
